import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, message, Form, Space, Select } from 'antd';
import { useDispatch } from 'react-redux';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { AppDispatch } from '../../store';
import { createPuja, addPujaToState } from '../../store/pujaSlice';
import { pujaApi, pujaTagAPI } from '../../services/api';

const { TextArea } = Input;
const { Option } = Select;

interface PujaParseModalProps {
  visible: boolean;
  onCancel: () => void;
}

const PujaParseModal: React.FC<PujaParseModalProps> = ({ visible, onCancel }) => {
  const [parseText, setParseText] = useState('');
  const [step, setStep] = useState<'input' | 'preview'>('input');
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [tags, setTags] = useState<any[]>([]);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (visible) {
      loadTags();
    }
  }, [visible]);

  const loadTags = async () => {
    try {
      const response = await pujaTagAPI.getAll();
      setTags(response.data);
    } catch (error) {
      console.error('Error loading tags:', error);
    }
  };

  const parsePujaText = (text: string) => {
    const lines = text.split('\n');
    const pujas: any[] = [];
    let currentPuja: any = null;
    let isFirstLineOfPuja = true;

    for (const line of lines) {
      const trimmedLine = line.trim();

      if (trimmedLine === '') {
        if (currentPuja) {
          pujas.push(currentPuja);
          currentPuja = null;
          isFirstLineOfPuja = true;
        }
        continue;
      }

      const tagMatch = trimmedLine.match(/^(.+?)\s*\[([^\]]+)\]$/);

      if (tagMatch) {
        const content = tagMatch[1].trim();
        const tag = tagMatch[2].trim();

        if (currentPuja) {
          currentPuja.lines.push({
            content,
            metadata: [{ key: 'tag', value: tag }],
          });
        }
        isFirstLineOfPuja = false;
      } else {
        if (isFirstLineOfPuja) {
          currentPuja = {
            name: trimmedLine,
            lines: [],
          };
          isFirstLineOfPuja = false;
        } else if (currentPuja) {
          currentPuja.lines.push({
            content: trimmedLine,
            metadata: [],
          });
        }
      }
    }

    if (currentPuja) {
      pujas.push(currentPuja);
    }

    return pujas;
  };

  const handleParse = () => {
    if (!parseText.trim()) {
      message.error('Please enter text to parse');
      return;
    }

    const parsed = parsePujaText(parseText);
    if (parsed.length === 0) {
      message.error('No valid pujas found in the text');
      return;
    }

    form.setFieldsValue({ pujas: parsed });
    setStep('preview');
  };

  const handleCreate = async (values: any) => {
    setLoading(true);
    try {
      for (const pujaData of values.pujas) {
        const processedPuja = {
          name: pujaData.name,
          metadata: {
            bidha: pujaData.bidha?.trim() || pujaData.name,
          },
          lines:
            pujaData.lines
              ?.map((line: any, index: number) => {
                const metadata: Record<string, string> = {};
                if (line.metadata) {
                  line.metadata.forEach((item: { key: string; value: string }) => {
                    if (item.key && item.value) {
                      metadata[item.key] = item.value;
                    }
                  });
                }
                return {
                  content: line.content,
                  order: index + 1,
                  isVisible: true,
                  metadata,
                };
              })
              .filter((line: any) => line.content?.trim()) || [],
        };

        const response = await dispatch(createPuja(processedPuja)).unwrap();
        const tagIds: number[] =
          pujaData.tagIds?.filter((id: any) => id !== undefined && id !== null) || [];

        if (tagIds.length > 0 && response.id) {
          try {
            await Promise.all(
              tagIds.map(tagId => pujaTagAPI.associateTagWithPuja(tagId, response.id!)),
            );
          } catch (tagError) {
            console.error('Error associating tags:', tagError);
            message.warning('Puja created but some tags may not have been associated');
          }
        }

        if (response.id) {
          try {
            const completePujaResponse = await pujaApi.getById(response.id);
            dispatch(addPujaToState(completePujaResponse.data));
          } catch (fetchError) {
            console.error('Error fetching complete puja:', fetchError);
            dispatch(addPujaToState(response));
          }
        }
      }

      message.success(`${values.pujas.length} puja(s) created successfully`);
      setParseText('');
      form.resetFields();
      setStep('input');
      onCancel();
    } catch (error) {
      console.error('Error creating pujas:', error);
      message.error('Failed to create pujas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={step === 'input' ? 'Parse Puja' : 'Review Parsed Pujas'}
      open={visible}
      onCancel={() => {
        onCancel();
        setStep('input');
        form.resetFields();
      }}
      width={800}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            onCancel();
            setStep('input');
            form.resetFields();
          }}
        >
          Cancel
        </Button>,
        step === 'input' ? (
          <Button key="parse" type="primary" onClick={handleParse}>
            Parse
          </Button>
        ) : (
          <Space key="preview-actions">
            <Button onClick={() => setStep('input')}>Back to Edit</Button>
            <Button type="primary" loading={loading} onClick={() => form.submit()}>
              Create All
            </Button>
          </Space>
        ),
      ]}
    >
      {step === 'input' ? (
        <>
          <div style={{ marginBottom: 16 }}>
            <p><strong>Format:</strong></p>
            <pre style={{ fontSize: '12px', color: '#666' }}>
{`puja 1 name
puja 1 line 1 [tag1]
puja 1 line 2
puja 1 line 3 [tag3]

puja 2 name
puja 2 line 1 [tag1]
puja 2 line 2 [tag2]
puja 2 line 3 [tag3]`}
            </pre>
            <p style={{ fontSize: 12, color: '#666', marginBottom: 0 }}>
              First line of each block is the puja name. Lines ending with <code>[tag]</code> get that tag in line metadata. Blank lines separate pujas. Assign puja-level tags in the review step.
            </p>
          </div>

          <TextArea
            placeholder="Enter puja text in the format shown above..."
            value={parseText}
            onChange={e => setParseText(e.target.value)}
            rows={15}
            style={{ fontFamily: 'monospace' }}
          />
        </>
      ) : (
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.List name="pujas">
            {(pujaFields, { add: addPuja, remove: removePuja }) => (
              <>
                {pujaFields.map(({ key: pujaKey, name: pujaName, ...pujaRestField }) => (
                  <div
                    key={pujaKey}
                    style={{
                      marginBottom: 24,
                      border: '2px solid #1890ff',
                      padding: 16,
                      borderRadius: 8,
                    }}
                  >
                    <Form.Item
                      {...pujaRestField}
                      name={[pujaName, 'name']}
                      label="Puja Name"
                      rules={[{ required: true, message: 'Please enter puja name' }]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      {...pujaRestField}
                      name={[pujaName, 'bidha']}
                      label="Bidha (metadata)"
                      tooltip="Defaults to puja name if left empty"
                    >
                      <Input placeholder="Defaults to puja name" />
                    </Form.Item>

                    <Form.Item label="Tags">
                      <Form.List name={[pujaName, 'tagIds']}>
                        {(tagFields, { add: addTag, remove: removeTag }) => (
                          <>
                            {tagFields.map(({ key: tagKey, name: tagName, ...tagRestField }) => (
                              <div key={tagKey} style={{ display: 'flex', marginBottom: 8 }}>
                                <Form.Item
                                  {...tagRestField}
                                  name={tagName}
                                  style={{ marginBottom: 0, flex: 1 }}
                                >
                                  <Select placeholder="Select tag" allowClear showSearch optionFilterProp="children">
                                    {tags.map(tag => (
                                      <Option key={tag.id} value={tag.id}>
                                        {tag.name}
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                                <Button
                                  onClick={() => removeTag(tagName)}
                                  style={{ marginLeft: 8 }}
                                  danger
                                >
                                  Remove
                                </Button>
                              </div>
                            ))}
                            <Button type="dashed" onClick={() => addTag()} block>
                              Add Tag
                            </Button>
                          </>
                        )}
                      </Form.List>
                    </Form.Item>

                    <Form.Item label="Lines">
                      <Form.List name={[pujaName, 'lines']}>
                        {(lineFields, { add: addLine, remove: removeLine }) => (
                          <>
                            {lineFields.map(({ key: lineKey, name: lineName, ...lineRestField }) => (
                              <div
                                key={lineKey}
                                style={{
                                  marginBottom: 16,
                                  border: '1px solid #d9d9d9',
                                  padding: 12,
                                  borderRadius: 6,
                                }}
                              >
                                <Form.Item
                                  {...lineRestField}
                                  name={[lineName, 'content']}
                                  label="Content"
                                  style={{ marginBottom: 8 }}
                                >
                                  <TextArea />
                                </Form.Item>

                                <Form.List name={[lineName, 'metadata']}>
                                  {(metaFields, { add: addMeta, remove: removeMeta }) => (
                                    <>
                                      {metaFields.map(({ key: metaKey, name: metaName, ...metaRestField }) => (
                                        <div
                                          key={metaKey}
                                          style={{
                                            display: 'flex',
                                            gap: 8,
                                            marginBottom: 8,
                                            alignItems: 'flex-start',
                                          }}
                                        >
                                          <Form.Item
                                            {...metaRestField}
                                            name={[metaName, 'key']}
                                            rules={[{ required: true, message: 'Key required' }]}
                                            style={{ flex: 1, marginBottom: 0 }}
                                          >
                                            <Input placeholder="Key" />
                                          </Form.Item>
                                          <Form.Item
                                            {...metaRestField}
                                            name={[metaName, 'value']}
                                            rules={[{ required: true, message: 'Value required' }]}
                                            style={{ flex: 1, marginBottom: 0 }}
                                          >
                                            <Input placeholder="Value" />
                                          </Form.Item>
                                          <Button
                                            type="text"
                                            danger
                                            icon={<MinusCircleOutlined />}
                                            onClick={() => removeMeta(metaName)}
                                            style={{ marginTop: 4 }}
                                          />
                                        </div>
                                      ))}
                                      <div style={{ textAlign: 'center', marginTop: metaFields.length > 0 ? 8 : 4 }}>
                                        <Button
                                          type="dashed"
                                          onClick={() => addMeta()}
                                          icon={<PlusOutlined />}
                                          size="small"
                                        >
                                          Additional Metadata
                                        </Button>
                                      </div>
                                    </>
                                  )}
                                </Form.List>

                                <Button
                                  onClick={() => removeLine(lineName)}
                                  danger
                                  size="small"
                                  style={{ marginTop: 8 }}
                                >
                                  Remove Line
                                </Button>
                              </div>
                            ))}
                            <Button type="dashed" onClick={() => addLine()} block>
                              Add Line
                            </Button>
                          </>
                        )}
                      </Form.List>
                    </Form.Item>

                    <Button onClick={() => removePuja(pujaName)} danger style={{ marginTop: 8 }}>
                      Remove Puja
                    </Button>
                  </div>
                ))}
                <Button type="dashed" onClick={() => addPuja()} block>
                  Add Puja
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      )}
    </Modal>
  );
};

export default PujaParseModal;
